import React, { useState, useEffect } from 'react'
import { withStyles } from '@mui/styles'
import { Box, TextField, Typography } from '@mui/material'

import { filterBasicInputStyles } from './filter-basic-input.styles'
import { useTypingRefresh } from '../../core/utils/useTypingRefresh'
import { useNavigate, useLocation } from 'react-router-dom'

const DEFAULT_TYPE_REFRESH_MS = 800

/**
 * @typedef {object} FilterBasicInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {number} [typeRefresh=800] - Tiempo en milisegundos para ejecutar la búsqueda tras dejar de escribir.
 * @property {string} name - Nombre del parámetro de búsqueda en la URL.
 * @property {string} label - Etiqueta mostrada antes del input.
 */

/**
 * Campo de entrada para filtrar contenido con actualización automática de URL.
 *
 * Input que actualiza la URL automáticamente tras un período de inactividad (typeRefresh),
 * sincroniza el valor del input con el parámetro de URL correspondiente, y reinicia la paginación
 * al cambiar el filtro. Utiliza useTypingRefresh para debounce.
 *
 * @component
 * @example
 * <FilterBasicInput
 *   name="search"
 *   label="Buscar"
 *   typeRefresh={1000}
 * />
 *
 * @param {FilterBasicInputProps} props - Las props del componente FilterBasicInput.
 */
export const FilterBasicInput = withStyles(filterBasicInputStyles)(({
  classes,
  typeRefresh = DEFAULT_TYPE_REFRESH_MS,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const currentSearch = new URLSearchParams(location.search)
  const searchQuery = currentSearch.get(props.name)

  useEffect(() => setInputValue(searchQuery ? searchQuery : ''), [searchQuery])

  const changeSearchValue = (search) => {
    if ((!search || search.length === 0) && !currentSearch.get(props.name)) {
      // Do nothing
    } else {
      currentSearch.set(props.name, search)
      currentSearch.set('_pagina', 1)
      navigate({
        search: '?' + currentSearch.toString(),
      })
    }
  }

  const { ref: textRef } = useTypingRefresh({
    timeout: typeRefresh,
    action: (val) => changeSearchValue(val),
    textFromRef: (ref) => (ref.current ? ref.current.querySelector('input') : null),
  })
  return (
    <Box style={{ flex: 1 }} ref={textRef}>
      <TextField
        className={classes.root}
        variant="outlined"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        InputProps={{
          className: classes.input,
          startAdornment: (
            <Typography variant="body1" className={classes.title}>
              {props.label}:
            </Typography>
          ),
        }}
      />
    </Box>
  )
})
